import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { ArrowLeft, FileSpreadsheet, FileText, CheckSquare, Square } from 'lucide-react';
import { btn, btnVoltar, sel, aviso, tabela, td, thEscuro, Girando, COR_MODULO } from './escala-ui';

// =====================================================================
// Dimensionamento de Escala — EXPORTAR
// =====================================================================
// São DOIS relatórios, e eles não são duas versões da mesma coisa (Rodrigo,
// 15/09):
//
//   1. O EXCEL, nas colunas da planilha `Escalas_Ideal` — o formato que começou
//      tudo isto. Vai para a mão de quem dimensiona, e por isso sai em LOTE:
//      várias lojas num arquivo só, que é como a planilha sempre foi.
//      Vem da view `vwRhEscalaLinhaDetalhe` e é montado no servidor
//      (`excel-escala.js`), que devolve o .xlsx pronto.
//
//   2. O PDF A4 PAISAGEM, uma folha por período, com a grade desenhada no mesmo
//      padrão gráfico da tela — é a folha que a LOJA olha. Um arquivo por
//      versão, porque é um documento, não uma base.
//
// ⚠️ A tela é uma só, com uma lista de seleção compartilhada, porque a pergunta
//    "quais escalas?" é a mesma nos dois casos. O que muda é o que sai.

const API = '/api/rh/dimensionamento-escala';
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });

const statusStyle = (s) => {
    if (s === 'Publicada') return { bg: '#d1fae5', color: '#065f46' };
    if (s === 'Rascunho') return { bg: '#fef3c7', color: '#92400e' };
    return { bg: '#e2e8f0', color: '#475569' };
};

const RHDimensionamentoEscalaExportar = ({ onSair }) => {
    const [versoes, setVersoes] = useState([]);
    const [marcadas, setMarcadas] = useState([]);
    const [filtroLoja, setFiltroLoja] = useState('');
    const [filtroStatus, setFiltroStatus] = useState('');
    const [carregando, setCarregando] = useState(true);
    const [trabalhando, setTrabalhando] = useState('');
    // ⚠️ O PDF leva SEGUNDOS por arquivo (o servidor abre um Chromium para cada
    //    um). Num lote de 12, "Gerando…" parado por meio minuto parece travado —
    //    o contador é o que diz que a fila está andando.
    const [progresso, setProgresso] = useState('');
    const [erro, setErro] = useState('');
    const [ok, setOk] = useState('');

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            const r = await fetch(`${API}/versoes`, { headers: auth() });
            if (!r.ok) throw new Error();
            setVersoes(await r.json());
        } catch {
            setErro('Não foi possível carregar a lista de versões.');
        } finally {
            setCarregando(false);
        }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    const lojas = useMemo(() => [...new Set(versoes.map((v) => v.LOJA))].sort(), [versoes]);

    const visiveis = useMemo(() => versoes.filter((v) =>
        (!filtroLoja || v.LOJA === filtroLoja) &&
        (!filtroStatus || v.STATUS === filtroStatus)
    ), [versoes, filtroLoja, filtroStatus]);

    // ⚠️ "Marcar todas" marca o que está VISÍVEL, não o banco inteiro: com um
    //    filtro na tela, marcar o que não se vê é a receita para exportar coisa
    //    que ninguém pediu.
    const todasVisiveisMarcadas = visiveis.length > 0 && visiveis.every((v) => marcadas.includes(v.ID));
    const alternarTodas = () => setMarcadas((m) => (todasVisiveisMarcadas
        ? m.filter((id) => !visiveis.some((v) => v.ID === id))
        : [...new Set([...m, ...visiveis.map((v) => v.ID)])]));

    const alternar = (id) => setMarcadas((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

    const marcadasObj = versoes.filter((v) => marcadas.includes(v.ID));

    // -----------------------------------------------------------------
    // 1) A tabela — .xlsx no formato da planilha
    // -----------------------------------------------------------------
    // ⚠️ O arquivo é montado NO SERVIDOR e chega pronto como binário. Antes a
    //    tela montava um CSV na mão; virou Excel de verdade a pedido (Rodrigo,
    //    15/09) e, com isso, quem sabe o formato passou a ser um lugar só —
    //    `server/rh/excel-escala.js`, que é quem conhece as colunas da planilha.
    const baixarTabela = async () => {
        setTrabalhando('xlsx'); setErro(''); setOk('');
        try {
            const r = await fetch(`${API}/exportar-lote?ids=${marcadas.join(',')}`, { headers: auth() });
            // ⚠️ O erro vem em JSON e o sucesso em binário — por isso o teste é
            //    pelo `ok`, e não por tentar ler o corpo primeiro.
            if (!r.ok) {
                const d = await r.json().catch(() => ({}));
                throw new Error(d?.message || 'Erro ao exportar.');
            }

            const url = URL.createObjectURL(await r.blob());
            const a = document.createElement('a');
            a.href = url;
            a.download = `escalas_${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);   // sem isto o blob fica na memória da aba

            // ⚠️ O aviso das versões VAZIAS viaja em header, porque o corpo é o
            //    arquivo. Vem em `encodeURIComponent` — header HTTP é ASCII.
            const linhas = r.headers.get('X-Escala-Linhas') || '?';
            const vazias = decodeURIComponent(r.headers.get('X-Escala-Vazias') || '');
            setOk(`${linhas} linha(s) de ${marcadas.length} versão(ões) no arquivo.`
                + (vazias ? ` Sem nenhuma linha: ${vazias}.` : ''));
        } catch (e) {
            setErro(e.message || 'Erro ao exportar.');
        } finally {
            setTrabalhando(''); setProgresso('');
        }
    };

    // -----------------------------------------------------------------
    // 2) O PDF — um por versão
    // -----------------------------------------------------------------
    // ⚠️ Um arquivo por versão, baixados em sequência. Quando são vários, o
    //    NAVEGADOR pergunta se aceita "múltiplos downloads" — é dele, não do
    //    portal, e por isso o aviso aparece escrito na tela.
    const baixarPdfs = async () => {
        setTrabalhando('pdf'); setErro(''); setOk('');
        let feitos = 0;
        try {
            for (const v of marcadasObj) {
                setProgresso(`${v.LOJA} · ${v.NOME_VERSAO} (${feitos + 1} de ${marcadasObj.length})`);
                const r = await fetch(`${API}/versoes/${v.ID}/pdf`, { headers: auth() });
                if (!r.ok) {
                    const d = await r.json().catch(() => ({}));
                    throw new Error(d?.message || `Erro ao gerar o PDF da ${v.LOJA} · ${v.NOME_VERSAO}.`);
                }
                const url = URL.createObjectURL(await r.blob());
                const a = document.createElement('a');
                a.href = url;
                a.download = `escala_${v.LOJA}_${String(v.NOME_VERSAO).replace(/\W+/g, '_')}.pdf`;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
                feitos += 1;
            }
            setOk(`${feitos} PDF(s) gerado(s).`);
        } catch (e) {
            // Conta quantos já saíram: numa falha no 5º de 12, saber que 4 estão
            // na pasta evita a pessoa refazer tudo.
            setErro(`${e.message}${feitos ? ` (${feitos} já haviam sido gerados)` : ''}`);
        } finally {
            setTrabalhando(''); setProgresso('');
        }
    };

    const nenhuma = marcadas.length === 0;

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                <button onClick={onSair} style={btnVoltar}><ArrowLeft size={16} /> Voltar</button>
                <div style={{ fontWeight: 800, fontSize: 16 }}>Exportar escalas</div>
            </div>

            {erro && <div style={aviso('#fee2e2', '#991b1b')}>{erro}</div>}
            {ok && <div style={aviso('#dcfce7', '#166534')}>{ok}</div>}

            {/* ---------- Os dois relatórios ---------- */}
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 18 }}>
                <div style={cartao}>
                    <div style={cartaoTitulo}><FileSpreadsheet size={16} color={COR_MODULO} /> Planilha (Excel)</div>
                    <p style={cartaoTexto}>
                        Um <b>.xlsx</b> no formato da planilha: loja, período, cargo, escala, os quatro
                        horários (entrada, saída para o break, volta e saída), quantidade e horas.
                        Várias versões saem num <b>arquivo só</b>, e uma aba <b>INFO</b> registra o que
                        foi exportado.
                    </p>
                    <button onClick={baixarTabela} disabled={nenhuma || !!trabalhando} style={botaoPrincipal(nenhuma || !!trabalhando)}>
                        {trabalhando === 'xlsx' ? <Girando size={14} /> : <FileSpreadsheet size={14} />}
                        {trabalhando === 'xlsx' ? 'Montando a planilha…' : `Baixar Excel${nenhuma ? '' : ` (${marcadas.length})`}`}
                    </button>
                </div>

                <div style={cartao}>
                    <div style={cartaoTitulo}><FileText size={16} color={COR_MODULO} /> Escala gráfica (PDF A4)</div>
                    <p style={cartaoTexto}>
                        <b>Uma folha por período</b>, em paisagem: a grade desenhada hora a hora no mesmo
                        padrão da tela, o quadro do efetivo ao lado e a identificação da versão (loja,
                        status, quem criou e quando). <b>Um arquivo por versão.</b>
                    </p>
                    <button onClick={baixarPdfs} disabled={nenhuma || !!trabalhando} style={botaoPrincipal(nenhuma || !!trabalhando)}>
                        {trabalhando === 'pdf' ? <Girando size={14} /> : <FileText size={14} />}
                        {trabalhando === 'pdf' ? 'Gerando…' : `Baixar PDF${nenhuma ? '' : ` (${marcadas.length})`}`}
                    </button>
                    {trabalhando === 'pdf' && progresso && (
                        <div style={{ fontSize: 11.5, color: '#475569', marginTop: 6, fontWeight: 700 }}>{progresso}</div>
                    )}
                    {marcadas.length > 1 && (
                        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
                            São {marcadas.length} arquivos — o navegador pode pedir permissão para vários downloads.
                        </div>
                    )}
                </div>
            </div>

            {/* ---------- A seleção ---------- */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
                <label style={{ fontWeight: 700, fontSize: 13, color: '#475569' }}>Loja</label>
                <select value={filtroLoja} onChange={(e) => setFiltroLoja(e.target.value)} style={sel}>
                    <option value="">Todas</option>
                    {lojas.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
                <label style={{ fontWeight: 700, fontSize: 13, color: '#475569' }}>Status</label>
                <select value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} style={sel}>
                    <option value="">Todos</option>
                    <option value="Publicada">Publicada</option>
                    <option value="Rascunho">Rascunho</option>
                    <option value="Arquivada">Arquivada</option>
                </select>
                <div style={{ flex: 1 }} />
                <span style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                    {marcadas.length} de {versoes.length} marcada(s)
                </span>
            </div>

            {carregando ? (
                <p style={{ color: '#64748b' }}>Carregando…</p>
            ) : visiveis.length === 0 ? (
                <div style={aviso('#f1f5f9', '#475569')}>Nenhuma versão com esses filtros.</div>
            ) : (
                <div style={{
                    overflowX: 'auto', border: '1px solid #e2e8f0', borderRadius: 10,
                    // Mexer na seleção no meio de uma exportação mudaria o que
                    // está sendo gerado — e o arquivo sairia diferente do que a
                    // tela mostra.
                    opacity: trabalhando ? 0.55 : 1,
                    pointerEvents: trabalhando ? 'none' : 'auto',
                    transition: 'opacity .15s',
                }}>
                    <table style={{ ...tabela, borderRadius: 10, overflow: 'hidden' }}>
                        <thead>
                            <tr style={{ background: '#0f172a' }}>
                                <th style={{ ...thEscuro, cursor: 'pointer', width: 34 }} onClick={alternarTodas}
                                    title={todasVisiveisMarcadas ? 'Desmarcar as visíveis' : 'Marcar todas as visíveis'}>
                                    {todasVisiveisMarcadas ? <CheckSquare size={15} /> : <Square size={15} />}
                                </th>
                                <th style={thEscuro}>Loja</th>
                                <th style={thEscuro}>Versão</th>
                                <th style={thEscuro}>Status</th>
                                <th style={thEscuro}>Criada por</th>
                                <th style={thEscuro}>Data criação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {visiveis.map((v) => {
                                const st = statusStyle(v.STATUS);
                                const marcada = marcadas.includes(v.ID);
                                return (
                                    // A LINHA INTEIRA alterna a marcação — mirar um quadradinho de
                                    // 13px numa lista de 30 versões é trabalho desnecessário.
                                    <tr key={v.ID} onClick={() => alternar(v.ID)}
                                        style={{ cursor: 'pointer', background: marcada ? '#fef2f2' : '#fff' }}>
                                        <td style={td}>
                                            <input type="checkbox" checked={marcada} readOnly />
                                        </td>
                                        <td style={{ ...td, fontWeight: 800 }}>{v.LOJA}</td>
                                        <td style={{ ...td, fontWeight: 700, letterSpacing: 0.3 }}>{v.NOME_VERSAO}</td>
                                        <td style={td}>
                                            <span style={{ background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 700 }}>
                                                {v.STATUS}
                                            </span>
                                        </td>
                                        <td style={td}>{v.USUARIO_CRIACAO || '—'}</td>
                                        <td style={td}>{new Date(v.DATA_CRIACAO).toLocaleDateString('pt-BR')}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
                Clique na linha para marcar. A tabela sai da view <b>vwRhEscalaLinhaDetalhe</b> — a mesma que
                responde à consulta direta no banco e ao Power BI, então o arquivo e o BI nunca divergem.
            </p>
        </div>
    );
};

const cartao = {
    flex: '1 1 300px', minWidth: 280, background: '#fff', border: '1px solid #e2e8f0',
    borderRadius: 10, padding: '14px 16px',
};
const cartaoTitulo = { display: 'flex', alignItems: 'center', gap: 7, fontWeight: 800, fontSize: 14, marginBottom: 6 };
const cartaoTexto = { margin: '0 0 12px', fontSize: 12.5, color: '#475569', lineHeight: 1.55 };
const botaoPrincipal = (desabilitado) => ({
    ...btn(desabilitado ? '#e2e8f0' : COR_MODULO, desabilitado ? '#94a3b8' : '#fff'),
    cursor: desabilitado ? 'not-allowed' : 'pointer',
});

export default RHDimensionamentoEscalaExportar;
